/**
 * Modèle de navigation du module Qualité — SOURCE UNIQUE des entrées de la
 * barre latérale ET des écrans proposés par la palette de recherche.
 *
 * Pourquoi un modèle plutôt que du JSX aligné dans `Shell.tsx` : la barre avait
 * atteint 42 entrées à plat, empilées dans l'ordre de livraison des lots. On ne
 * retrouvait plus un écran sans le chercher des yeux. Les regrouper suppose de
 * pouvoir répondre à trois questions AILLEURS que dans le rendu — quelles
 * entrées existent, laquelle est visible pour ce profil, et à quelle famille
 * elle appartient. La palette pose exactement les mêmes questions ; les faire
 * répondre deux fois aurait garanti la divergence.
 *
 * ⚠️ Le regroupement ne donne AUCUN droit. `visible` reprend, à l'identique,
 * les conditions qui filtraient déjà chaque entrée. Une famille dont toutes les
 * entrées sont masquées ne doit pas apparaître — sans quoi le simple titre
 * « Personnel & plannings » révélerait à un technicien l'existence d'écrans de
 * paie qu'il ne peut pas ouvrir.
 */
import type { GedView, GedSession } from '@/lib/types';

/** Ce dont dépend la visibilité d'une entrée — rien d'autre n'entre ici. */
export type DroitsNav = {
  /** Responsable qualité : voit le retrait d'urgence et les demandes en attente. */
  isRQ: boolean;
  /** Gestionnaire RH (forfait ∩ rôle `rh`) : toute la rubrique plannings/paie. */
  rhAcces: boolean;
  /**
   * Droit PAR SECTION tel que le MOTEUR l'a calculé (lot N6). C'est lui qui
   * fait foi dès qu'il répond, parce que le paramétrage vit côté serveur : une
   * condition réécrite ici finirait par dire autre chose que la garde.
   */
  section: (code: string, acte?: 'voir' | 'modifier') => boolean;
  /** Portée de lecture TOUTES LIGNES (et non « la sienne ») — « Dossiers RH »
   *  n'a de sens que pour qui voit tout le personnel. */
  porteeTout: (code: string) => boolean;
};

/**
 * Construit les droits de navigation à partir de la session.
 *
 * ⚠️ Le repli quand `session.sections` est absent n'est pas un détail : il
 * couvre le temps entre le déploiement de l'interface et celui du moteur, et le
 * cas d'une session chargée avant la bascule. Sans lui, la barre latérale se
 * viderait pour tout le monde pendant ce laps de temps — un durcissement de
 * droits qui rend les écrans muets à des gens qui travaillaient la veille est
 * exactement ce que ce lot doit éviter.
 */
export function droitsNav(session: GedSession, rhAcces: boolean): DroitsNav {
  const isRQ = session.roles.includes('rq');
  const s = session.sections;
  return {
    isRQ,
    rhAcces,
    /* Le moteur a calculé (`GET /ged/me`, modèle des rôles) ; l'écran ne
       recalcule pas. Session pas encore chargée → rien n'est visible : une
       barre vide une seconde vaut mieux qu'une rubrique montrée à tort. */
    section: (code, acte = 'voir') => {
      const d = s?.[code];
      if (!d) return false;
      return acte === 'voir' ? d.voir || d.portee === 'moi' : d.modifier;
    },
    porteeTout: (code) => s?.[code]?.portee === 'tout',
  };
}

export type EntreeNav = {
  view: GedView;
  icon: string;
  label: string;
  /**
   * Vocabulaire du terrain que le libellé ne porte pas : sigles, synonymes,
   * mots que la personne tape SPONTANÉMENT. « congés » doit trouver le
   * planning, « CQI » le contrôle interne, « thermomètre » la métrologie.
   * Ces mots ne s'affichent jamais — ils ne servent qu'à la palette.
   */
  synonymes?: string;
  /** Section de rattachement quand elle ne porte pas le nom de la vue (lot N6).
   *  Absent = le code de la vue EST le code de la section. */
  section?: string;
  /** Absent = visible par tout profil ayant accès au module. */
  visible?: (d: DroitsNav) => boolean;
  /**
   * Hors barre latérale, mais TOUJOURS dans la palette de recherche (12/08/2026).
   *
   * « Mes congés », « Mon planning » et « Mon dossier RH » ont rejoint le menu
   * du profil : ils ne parlent que de la personne connectée, et voisinaient
   * dans la barre avec le planning de tout le laboratoire et la paie. Les
   * retirer PUREMENT du modèle les aurait aussi retirés de la recherche — or
   * quelqu'un qui tape « congés » doit les trouver, où qu'ils vivent. La
   * palette les garde donc, avec la mention de leur nouvel emplacement.
   */
  horsBarre?: boolean;
};

export type FamilleNav = {
  /** Clé de persistance du repli — ne jamais la renommer sans migration. */
  id: string;
  titre: string;
  icon: string;
  entrees: EntreeNav[];
};

/**
 * Écran d'accueil, ÉPINGLÉ hors des familles.
 *
 * Le tableau de bord n'est pas un écran « de la famille X » : c'est le point de
 * retour. L'enfermer dans une famille repliable aurait permis de le faire
 * disparaître de la barre — le seul écran dont ça n'a jamais de sens.
 */
export const ACCUEIL: EntreeNav = {
  view: 'dash', icon: 'dash', label: 'Tableau de bord',
  synonymes: 'accueil synthese indicateurs pilotage vue generale home',
};

/**
 * Les cinq familles, dans l'ordre d'usage — pas dans l'ordre des lots.
 *
 * Le critère de regroupement est CE QUE LA PERSONNE VIENT FAIRE, et non le
 * chapitre de la norme ni la date de livraison : c'est pour ça que « Critères
 * d'acceptation » est rangé avec les non-conformités (on applique la grille,
 * le refus qui en découle EST l'écart suivant) et que « Correspondance ISO »
 * est rangé avec les documents (on y vient pour retrouver une pièce, pas pour
 * piloter).
 */
export const FAMILLES_NAV: FamilleNav[] = [
  {
    id: 'documents',
    titre: 'Documents & diffusion',
    icon: 'famDocuments',
    entrees: [
      { view: 'list', icon: 'doc', label: 'Documents', synonymes: 'procedure mode operatoire instruction formulaire enregistrement ged fiche technique version' },
      { view: 'req', icon: 'req', label: 'Demandes', synonymes: 'demande de modification creation revision validation circuit' },
      { view: 'diff', icon: 'diff', label: 'Diffusion & accusés', synonymes: 'accuse de lecture emargement lu et approuve destinataires' },
      { view: 'retrait', icon: 'alert', label: 'Retrait d’urgence', synonymes: 'retirer perimee obsolete rappel urgence', visible: (d) => d.isRQ && d.section('retrait') },
      { view: 'proc', icon: 'process', label: 'Processus', synonymes: 'cartographie processus pilote interactions' },
      { view: 'auditview', icon: 'eye', label: 'Vue auditeur', synonymes: 'auditeur cofrac inspection visite preuve dossier accreditation' },
      { view: 'iso', icon: 'book', label: 'Correspondance ISO', synonymes: 'iso 15189 norme chapitre exigence paragraphe couverture' },
    ],
  },
  {
    id: 'ecarts',
    titre: 'Écarts & amélioration',
    icon: 'famEcarts',
    entrees: [
      { view: 'nc', icon: 'alert-triangle', label: 'Non-conformités', synonymes: 'nc anomalie incident ecart dysfonctionnement erreur' },
      { view: 'actions', icon: 'capa', label: 'Plan d’actions', synonymes: 'action corrective preventive capa echeance suivi efficacite' },
      { view: 'recl', icon: 'mail', label: 'Réclamations', synonymes: 'plainte patient prescripteur mecontentement retour client' },
      { view: 'suggestions', icon: 'bulb', label: 'Suggestions du personnel', synonymes: 'idee proposition boite a idees amelioration personnel' },
      { view: 'retours', icon: 'mail', label: 'Retours des usagers', synonymes: 'avis patient compliment remerciement satisfaction retour usager site public votre avis' },
      { view: 'rdv', icon: 'calendarUser', label: 'Demandes de rendez-vous', synonymes: 'rendez-vous rdv demande prise de sang domicile patient site ordonnance creneau' },
    ],
  },
  {
    id: 'pilotage',
    titre: 'Pilotage & revue',
    icon: 'famPilotage',
    entrees: [
      { view: 'audits', icon: 'clipboardCheck', label: 'Audits internes', synonymes: 'audit interne programme constat ecart auditeur planning audit' },
      // Rangée SOUS les audits internes et jamais confondue avec eux : c'est le
      // même sujet pour l'utilisateur, deux registres pour la norme (§8.8).
      { view: 'autoeval', icon: 'selfCheck', label: 'Auto-évaluation de paillasse', synonymes: 'autoevaluation auto controle paillasse poste technicien verification quotidienne' },
      { view: 'risques', icon: 'scale', label: 'Risques & opportunités', synonymes: 'risque opportunite criticite amdec maitrise' },
      { view: 'objectifs', icon: 'target', label: 'Objectifs qualité', synonymes: 'objectif indicateur cible kpi politique qualite' },
      { view: 'kpi', icon: 'chart', label: 'Indicateurs — suivi', synonymes: 'kpi indicateur graphe courbe tendance suivi activite statistique' },
      { view: 'continuite', icon: 'lifebuoy', label: 'Plan de continuité', synonymes: 'continuite panne secours repli sinistre pca crise' },
      { view: 'rdd', icon: 'stamp', label: 'Revue de direction', synonymes: 'rdd revue de direction compte rendu bilan annuel decisions' },
      /* Rangée AVEC la revue de direction et les audits : c'est une pièce
         qu'on ouvre pour préparer une revue ou recevoir un évaluateur, pas un
         outil de mise au point. */
      { view: 'coherence', icon: 'shield', label: 'Cohérence du modèle', synonymes: 'coherence incoherence ecart modele donnees doublon initiales pilote orphelin controle audit qualite', section: 'iso' },
      /* ── LE CENTRE DES NOTIFICATIONS (06/10/2026) ───────────────────────────
         Rangé ici, et DANS LA BARRE — pas en `horsBarre`.

         La famille en comptait huit, le cliquet `front-familles-nav` en permet
         dix : il y avait la place, et c'est le seul argument qui aurait pu
         justifier de le cacher. Un écran qu'on ouvre pour répondre à « pourquoi
         le labo n'a-t-il rien reçu ? » doit se trouver sans qu'on sache déjà
         qu'il existe — c'est exactement le défaut qui a laissé
         `PersonnalisationTab` introuvable pendant des semaines.

         ⚠️ `section: 'notifs'` — la rubrique est fermée au seul rôle `rq`
         (`backend/src/ged-sections.ts`). L'entrée disparaît donc pour les
         autres : le registre nomme les boîtes du laboratoire et dit lesquelles
         refusent les messages. */
      { view: 'notifs', icon: 'mail', label: 'Notifications & envois', synonymes: 'notification notifications alerte alertes mail e-mail courriel envoi envois destinataire regle rendez-vous avis usager demande de compte registre echec smtp non recu previens prevenu', section: 'notifs' },
    ],
  },
  {
    id: 'technique',
    titre: 'Parc, métrologie & achats',
    icon: 'famTechnique',
    entrees: [
      { view: 'equip', icon: 'automate', label: 'Équipements', synonymes: 'automate appareil machine parc materiel fiche de vie inventaire' },
      { view: 'metro', icon: 'shield', label: 'Métrologie — raccordement', synonymes: 'metrologie raccordement etalon thermometre pipette centrifugeuse enceinte temperature verification' },
      { view: 'maintenances', icon: 'wrench', label: 'Maintenance préventive', synonymes: 'maintenance entretien preventive curative panne intervention technicien' },
      { view: 'relevetemp', icon: 'thermo', label: 'Relevés de température', synonymes: 'temperature releve enceinte refrigerateur congelateur etuve mini maxi thermometre plage conservation excursion chaine du froid sonde tournee' },
      { view: 'revuetiers', icon: 'scale', label: 'Revue de contrat & évaluations', synonymes: 'revue contrat convention evaluation fournisseur sous-traitant laboratoire accreditation portee periodicite echeance tiers juger qualifie suspendu' },
    ],
  },
  {
    /* ── MATÉRIEL, FAMILLE À PART ENTIÈRE (RQ, 10/10/2026) ───────────────────
     * « Matériel doit être une entité à part entière comme Documents &
     * diffusion. Dedans, je retrouve Stock qui contient tous les réactifs y
     * compris les contrôles et calibrants, qui sont juste une sous-catégorie.
     * Les aliquots sont visibles dans l'outil Fractionnement. »
     * Les lieux de stockage, eux, sont un RÉFÉRENTIEL : ils sont partis au
     * paramétrage. Les `view` ne changent pas (adresses partagées) : seul le
     * rangement et les libellés. */
    id: 'materiel',
    titre: 'Matériel',
    icon: 'famMateriel',
    entrees: [
      { view: 'produits', icon: 'book', label: 'Catalogue des produits', synonymes: 'produit catalogue reference fournisseur reactif controle calibrant consommable fractionnement reconstitution capacite' },
      { view: 'fourn', icon: 'flask', label: 'Stock', synonymes: 'stock materiel reactif lot peremption reception quarantaine consommable achat fournisseur controle calibrant reconstitution equipement' },
      { view: 'mat', icon: 'droplet', label: 'Fractionnement', synonymes: 'fractionnement aliquot cupule portion destockage scan congelateur decongelation stabilite etiquette' },
      { view: 'stock', icon: 'archive', label: 'Prêts & distribution de tubes', synonymes: 'pret emprunt tube distribution inventaire mission logistique magasin' },
    ],
  },
  {
    /* ── CETTE FAMILLE EST NÉE D'UNE COUPE, LE 12/09/2026 (retour 023) ──────
     *
     * « On se perd dans les modules tellement il y en a. Me proposer une
     *  solution. » — RQ, 11/09.
     *
     * Le défaut n'était pas la présentation : les familles étaient déjà nommées
     * et repliables. C'était qu'UNE SEULE en portait **dix-sept** — un tiers de
     * l'application derrière un seul repli, quand les autres en comptaient cinq
     * à huit. Replier ne servait alors à rien : ce qu'on cherchait était
     * toujours dans le bloc ouvert.
     *
     * ⚠️ LA COUPE SUIT CE QU'ON VIENT FAIRE, pas le chapitre de la norme — même
     * critère que les cinq familles d'origine. D'un côté la JUSTESSE DES
     * RÉSULTATS : contrôles, performance, validation. De l'autre les OBJETS et
     * ce qu'on y consomme : parc, métrologie, maintenance, achats.
     *
     * ⚠️ CALIBRATIONS EST ICI, et ce n'est pas évident : on calibre un
     * APPAREIL. Mais on ne le calibre jamais pour lui-même — on le calibre
     * parce qu'une carte a décroché, et l'écran s'ouvre d'ailleurs depuis un
     * point de CQI (`CalibrationDepuisCqi`). Le ranger au parc séparerait le
     * geste de sa cause.
     *
     * ⚠️ MATÉRIAUX RECONSTITUÉS EST AU PARC, et ce n'est pas évident non plus :
     * ce sont des matériaux de CONTRÔLE. Mais ce qu'on vient y faire est de la
     * logistique — réception, reconstitution, péremption —, et c'est à côté des
     * réactifs et du stock que la question se pose.
     *
     * ⚠️ NI CODE COULEUR NI MENU AU SURVOL — les deux pistes proposées le 11/09,
     * écartées avec la RQ le 12/09. La couleur redirait ce que le titre dit
     * déjà, et six teintes concurrentes ajoutent du bruit sans nouveau repère ;
     * un menu au survol ferait perdre le seul avantage réel de la barre — voir
     * en permanence où l'on est — et reste hostile au tactile et au clavier.
     *
     * ⚠️ AUCUNE VUE N'A ÉTÉ RENOMMÉE. Les `view` sont des identifiants : ils
     * portent les URL partagées et les favoris. Seul le RANGEMENT change. */
    id: 'controles',
    titre: 'Contrôles & performance',
    icon: 'famControles',
    entrees: [
      /* Chantier CQ (07-08/10/2026) — l'écran TRANSVERSAL : tableau de bord,
         performance (Sigma), audit mesure par mesure, synthèse mensuelle. Quatre
         onglets sous UNE entrée : la famille plafonne à dix (`front-familles-nav`). */
      { view: 'cqtdb', icon: 'chart', label: 'Qualité analytique — Tableau de bord', synonymes: 'tableau de bord cq cqi pilotage indicateurs rejets alertes conformite performance sigma qgi tea audit synthese mensuelle' },
      { view: 'cqi', icon: 'chart', label: 'CQI — Contrôle interne', synonymes: 'cqi controle interne de qualite carte de controle levey jennings westgard derive' },
      { view: 'cqisaisie', icon: 'check', label: 'CQI — Saisie de la série', synonymes: 'cqi saisie serie passage controle valeur du jour' },
      /* ⚠️ « EEQ — Envois » et non « CQE » (arbitrage d'Inès du 27/08/2026,
         MAJ-10). La même réalité portait deux sigles : CQE pour ce qu'on
         expédie, EEQ pour les z-scores reçus. Un seul terme désormais, et c'est
         celui de l'édition 2022 de la norme.
         ⚠️ `cqe` reste dans les SYNONYMES : c'est le mot que le laboratoire a
         employé pendant des années, et la recherche doit continuer d'y répondre.
         Un renommage qui rend un écran introuvable par son ancien nom se paie
         en tickets, pas en clarté.
         ⚠️ Le nom de la VUE (`cqeenvois`), les chemins de route et les tables
         ne bougent PAS : ce sont des identifiants, pas des libellés. Les
         renommer casserait les liens déjà partagés pour un gain nul. */
      { view: 'cqeenvois', icon: 'send', label: 'EEQ — Envois', synonymes: 'eeq cqe envoi expedition echantillon controle externe evaluation campagne' },
      /* Les deux faces portent le même sigle depuis le 27/08/2026 : ce qu'on
         ENVOIE, et ce qu'on REÇOIT. Le couple se lit d'un coup d'œil dans le
         menu, là où « CQE » et « EEQ » se lisaient comme deux sujets. */
      { view: 'eeq', icon: 'dish', label: 'EEQ — Résultats reçus', synonymes: 'eeq evaluation externe de la qualite comparaison interlaboratoire z score organisme' },
      /* UNITÉ INDÉPENDANTE, et non un sous-onglet d'EEQ (demande de la RQ du
         28/08/2026). Les synonymes portent « cqe » et « eeq » parce que c'est
         par là qu'on le cherchera — mais le libellé, lui, ne dit jamais EEQ :
         la référence d'un EIL n'est assignée par personne. */
      { view: 'eil', icon: 'echange', label: 'EIL — Échange inter-laboratoires', synonymes: 'eil echange inter laboratoire local confrere pair comparaison consensus moyenne participants eeq cqe paludisme lame cortisol' },
      { view: 'cal', icon: 'scale2', label: 'Calibrations', synonymes: 'calibration etalonnage courbe calibrateur reglage' },
      /* Hors barre depuis le 18/10/2026 : atteint par la barre « Registres du
         référentiel » (RegistresLies), en tête du catalogue. Reste ici — sa
         famille, sa section, la palette — rien d'autre ne change. */
      { view: 'refperf', icon: 'sigma', label: 'Critères de performance', synonymes: 'performance seuil seuils seuil acceptable seuils sfbc exactitude fidelite referentiel catalogue', horsBarre: true },
      { view: 'dvm', icon: 'flask', label: 'Validation de méthode', synonymes: 'validation de methode verification portee dossier v0 v1 justesse repetabilite' },
      { view: 'incertitude', icon: 'wave', label: 'Incertitude de mesure', synonymes: 'incertitude de mesure u elargie k=2 gum fidelite intermediaire biais raccordement erreur totale' },
    ],
  },
  {
    id: 'personnel',
    titre: 'Personnel & compétences',
    icon: 'famPersonnel',
    entrees: [
      { view: 'org', icon: 'sitemap', label: 'Organigramme', synonymes: 'organigramme equipe hierarchie fonction poste responsable effectif' },
      /* « Matrice de compétences » et non « Habilitations » (RQ, 19/08/2026) :
       * c'est le nom que le laboratoire emploie, et il distingue cet écran —
       * qui dit QUI PEUT TENIR QUEL POSTE — du référentiel des habilitations,
       * qui dit sur quoi porte chaque compétence et comment elle s'acquiert. */
      { view: 'hab', icon: 'award', label: 'Compétences — qui tient quel poste', synonymes: 'matrice competence habilitation autorisation formation qualification poste qui peut tenir' },
      // Les trois entrées « de moi » vivent dans le menu du profil (le rond aux
      // initiales, en haut à droite) : `horsBarre` les y laisse tout en les
      // gardant trouvables à la recherche.
      { view: 'mesconges', icon: 'beach', label: 'Mes congés', synonymes: 'mes conges vacances demande absence repos rtt recuperation solde profil mon espace', visible: (d) => d.section('mesconges'), horsBarre: true },
      { view: 'monplanning', icon: 'calendarUser', label: 'Mon planning', synonymes: 'mon planning mes affectations mon poste ma semaine profil mon espace', horsBarre: true },
      { view: 'mespresences', icon: 'clock', label: 'Mes présences', synonymes: 'mes présences mon pointage mes badges badgeuse mes heures validé profil mon espace', horsBarre: true },
      /* Lot 5 (18/10/2026) : fondu dans « Mon parcours » — l'entrée reste pour la palette et les anciens liens, elle y mène. */
      { view: 'meshabilitations', icon: 'award', label: 'Mes habilitations (dans Mon parcours)', synonymes: 'mes habilitations mes competences mon niveau echeance renouvellement profil mon espace', horsBarre: true },
      { view: 'monparcours', icon: 'clipboardCheck', label: 'Mon parcours', synonymes: 'mon parcours habilitation formation tuteur integration livret lectures evaluations profil mon espace', horsBarre: true },
      { view: 'parcours', icon: 'clipboardCheck', label: 'Parcours d’habilitation', synonymes: 'parcours habilitation formation tuteur frise personne', visible: (d) => d.section('rhhab'), horsBarre: true },
      // Lot N6 — « chacun voit le sien ». L'entrée est ouverte à tout le
      // personnel : ce qui est filtré n'est pas la rubrique, c'est la LIGNE.
      // Le gestionnaire RH y accède aussi, et y trouve n'importe quel dossier.
      {
        view: 'mondossier', icon: 'suitcase', label: 'Mon dossier RH',
        synonymes: 'mon dossier rh contrat matricule badge anciennete solde conges personnel administratif profil mon espace',
        section: 'dossierRh', visible: (d) => d.section('dossierRh'), horsBarre: true,
      },
      /* DOSSIERS RH (RQ, 04/10/2026) — la liste du personnel et le dossier de
         chacun, dans la barre : on ne passe plus par « Mon dossier RH » (le
         profil) pour ouvrir celui des autres. Réservé au SEUL rôle `rh`
         (RQ : « Dossier RH est uniquement pour le RH »). */
      {
        view: 'dossiers', icon: 'suitcase', label: 'Dossiers RH',
        synonymes: 'dossiers rh dossier personnel salaries fiche contrat liste du personnel effectif',
        section: 'dossierRh', visible: (d) => d.rhAcces && d.porteeTout('dossierRh'),
      },
      /* ⚠️ « Référentiel » et non « Habilitations & compétences » (MAJ-09
         du 26/08/2026). Les deux entrées vivaient à huit lignes d'écart dans
         la même section, sous des noms quasi interchangeables — « Matrice de
         compétences » et « Habilitations & compétences » — alors qu'elles ne
         font pas la même chose : celle-ci CRÉE et archive les habilitations et
         dit qui les détient ; l'autre est une VUE qui dit qui peut tenir quel
         poste, et ne s'écrit plus depuis le 25/08. Le libellé dit maintenant
         laquelle est la source. */
      { view: 'rhhab', icon: 'matrix2', label: 'Habilitations — référentiel', synonymes: 'habilitation competence grille evaluation parcours integration rh', visible: (d) => d.section('rhhab') },
      { view: 'situations', icon: 'bulb', label: 'Mises en situation', synonymes: 'cas clinique qcm quiz evaluation maintien competence situation' },
      { view: 'rhjournal', icon: 'logRh', label: 'Journal RH', synonymes: 'journal rh trace audit historique evenement acte qui a fait quoi quand piste', visible: (d) => d.section('rhjournal') },
    ],
  },
  {
    id: 'plannings',
    titre: 'Calendrier & contacts',
    icon: 'famPlannings',
    entrees: [
      {
        view: 'presences', icon: 'clock', label: 'Présences',
        synonymes: 'presence pointage badge heures realisees anomalie journee',
        visible: (d) => d.section('presences'),
      },
      { view: 'planning', icon: 'history', label: 'Planning', synonymes: 'planning horaire roulement garde affectation conges equipe semaine ouverture poste', visible: (d) => d.section('planning') },
      { view: 'reglesplanning', icon: 'check', label: 'Règles d’affectation', synonymes: 'regle affectation planning previsionnel contrainte preference rotation binome exigence homme femme continuite', visible: (d) => d.section('planningprev') },
      { view: 'calendrier', icon: 'history', label: 'Calendrier', synonymes: 'calendrier agenda evenement convention clinique dialyse carthage debbabi passage tournee recurrence ferie', visible: (d) => d.section('planning') },
      /* Le CARNET DES TIERS — rangé ici, sous « Calendrier & contacts », le
         12/10/2026 (RQ : « cela ne concerne pas uniquement les achats »). Il
         était au parc, à côté de la revue de contrat (§6.7) qui, elle, y
         reste : celle-là juge une compétence, le carnet ne porte que
         l'identité et les coordonnées. C'est aussi le seul endroit où vivent une clinique
         conventionnée, un centre de dialyse ou un médecin prescripteur. */
      { view: 'correspondants', icon: 'handshake', label: 'Carnet de contacts', synonymes: 'correspondant externe fournisseur achats carnet contact adresse clinique dialyse medecin prescripteur preleveur centre convention releve mensuel tiers annuaire' },
      /* L'ANNUAIRE (lot 2, 20/10/2026) : le carnet unique qui remplacera le carnet
         de contacts à la bascule. Visible selon la rubrique — fermée par
         l'interrupteur de mise en service jusqu'à la recette. */
      { view: 'annuaire', icon: 'users', label: 'Annuaire', synonymes: 'annuaire medecin prescripteur laboratoire correspondant sous-traitant fournisseur interlocuteur clinique chu etablissement cabinet lieu exercice cnam matricule fiscal telephone contact doublon fusion', visible: (d) => d.section('annuaire') },
      { view: 'absences', icon: 'users', label: 'Absences & congés', synonymes: 'absence conge validation demandes maladie autorisation', visible: (d) => d.section('absences') },
      // « Postes de travail » est l'écran de RÉGLAGE des postes, pas leur
      // lecture : la barre le propose donc à qui peut les modifier. Leur
      // LECTURE, elle, est ouverte — c'est ce que le planning consomme.
      { view: 'postes', icon: 'matrix', label: 'Postes de travail', synonymes: 'poste de travail activite composition couverture', visible: (d) => d.section('postes', 'modifier') },
      /* La FICHE de poste n'a PAS d'entrée dans la barre (RQ, 19/08/2026 :
       * « pas besoin d'un bouton fiches de poste, je veux que ce soit présenté
       * quand on clique sur le poste »). Une liste de postes à côté d'une liste
       * de fiches de poste, c'est deux fois la même liste — et il faut alors
       * deviner de quel côté ouvrir.
       *
       * La vue reste ADRESSABLE (`/fiche-de-poste/CHIMIE`) et se garde
       * `horsBarre` : elle s'ouvre en cliquant un poste, et un lien envoyé par
       * courriel continue de fonctionner. */
      { view: 'fichePoste', icon: 'matrix', label: 'Fiche de poste', synonymes: 'fiche de poste mission competence exigence grille habilitation paillasse imprimer', visible: (d) => d.section('postes'), horsBarre: true },
      /* LES CHECK-LISTS DE POSTE (RQ, 05/10/2026) — elles n'avaient AUCUN
         écran : 141 tâches en dur dans le code. Hors barre comme la fiche de
         poste : on y vient depuis « Postes de travail », pas en navigant. */
      { view: 'checklists', icon: 'check', label: 'Check-lists de poste', synonymes: 'check list checklist liste de controle tache garde reception prelevement bacterio memo du jour pense-bete', visible: (d) => d.section('postes', 'modifier'), horsBarre: true },
      { view: 'paie', icon: 'coins', label: 'Paie', synonymes: 'paie salaire variable bulletin cloture mois', visible: (d) => d.section('paie') },
    ],
  },
  /* ── Chantier manuel-vitrine (lot V1, 15/08/2026) ─────────────────────────
   *
   * Le manuel de prélèvement se PARAMÈTRE ici : l'application « manuel » n'est plus
   * qu'une vitrine. Famille placée À LA SUITE de « Personnel & plannings » —
   * c'est la décision d'implantation de la propriétaire, et l'ordre de ce
   * tableau EST l'ordre de la barre latérale.
   *
   * Une seule entrée au V1 ; les registres voisins (techniques, intervalles,
   * critères, combos) sont des ONGLETS de cet écran, pas des rubriques : une
   * seule section (`manuel`) ferme tout le paramétrage d'un coup. */
  {
    id: 'manuel',
    titre: 'Catalogue des analyses',
    icon: 'famManuel',
    entrees: [
      {
        view: 'manuel', icon: 'tube', label: 'Référentiel des analyses',
        synonymes: 'manuel prelevement catalogue examen analyse fiche technique intervalle reference critere combo delai rendu batch vitrine'
          // Versés le 30/08/2026 avec la fermeture de « Paramètres & valeurs usuelles ».
          + ' analyte parametre valeurs usuelles normales revue biologique mesure calcule cqi carte controle interne critere acceptation',
      },
      /* Rapatriés ici le 17/08/2026. Les trois décrivent LE CATALOGUE — ce
         qu'on accepte de recevoir, les bornes qu'on rend, et ce qu'on mesure —
         et ils étaient dispersés dans trois familles étrangères : les critères
         chez « Écarts », les intervalles chez « Pilotage », les analytes au
         « Plateau technique ». On les cherchait partout sauf où ils portent
         leur sens.
         Ce sont des RUBRIQUES et non des onglets de l'écran « Référentiel » :
         chacune garde son code de section, donc ses droits propres — les
         fondre en onglets aurait ouvert ou fermé les trois d'un seul geste.
         ⚠️ HORS BARRE depuis le 18/10/2026 (Ines : « trop d'entrées voisines ») :
         un seul menu « Référentiel des analyses », et une barre « Registres du
         référentiel » (`RegistresLies`) en tête des quatre écrans mène aux
         trois autres — en ne montrant que ceux dont la section est ouverte.
         Les vues, les adresses et les sections sont inchangées. */
      { view: 'criteres', icon: 'filter', label: 'Critères d’acceptation', synonymes: 'preanalytique reception echantillon refus tube conformite prelevement referentiel catalogue', horsBarre: true },
      { view: 'revueref', icon: 'ruler', label: 'Intervalles de référence', synonymes: 'valeurs usuelles normes biologiques intervalle reference revue periodique referentiel catalogue', horsBarre: true },
      /* ⚠️ « Paramètres & valeurs usuelles » a été FERMÉ le 30/08/2026 : c'était
         une seconde porte sur le même type de contenu que le référentiel, et
         elle écrivait onze de ses champs sans version ni circuit éditorial. Ses
         deux rubriques utiles (cartes CQI, critères d'acceptation) et la
         déclaration de revue sont sur la fiche paramètre du référentiel.
         ⚠️ SES SYNONYMES ONT ÉTÉ VERSÉS À L'ENTRÉE « Référentiel des analyses »
         ci-dessus. Les perdre aurait rendu la recherche « analyte » ou « valeurs
         usuelles » muette — et une recherche muette ne dit pas « c'est
         ailleurs », elle dit « ça n'existe pas ». */
    ],
  },
  /* ── MoMent — dépistage prénatal (lot L1, 11/10/2026) ───────────────────
   * Le compte rendu de dépistage de la trisomie 21 : son référentiel d'abord
   * (paramètres d'analyse, commentaires types, règles), puis les dossiers et
   * la validation aux lots suivants. Famille À PART : ce n'est ni le catalogue
   * (qu'il LIT), ni le plateau technique. Même code de section pour toutes ses
   * entrées (`moment`) : fermer MoMent à quelqu'un ferme tout. */
  {
    id: 'moment',
    titre: 'MoMento — dépistage prénatal',
    icon: 'famMoment',
    entrees: [
      {
        view: 'momentdossiers', icon: 'famMoment', label: 'Dossiers MoMento',
        synonymes: 'moment dossier depistage trisomie t21 pdf ssdwlab import compte rendu cr validation patiente risque prenatal',
        section: 'moment', visible: (d) => d.section('moment'),
      },
      {
        view: 'moment', icon: 'book', label: 'Référentiel MoMento',
        synonymes: 'moment trisomie 21 t21 depistage prenatal aneuploidie ssdwlab risque marqueurs seriques pappa hcg afp clarte nucale commentaires types regles validation compte rendu cr',
        visible: (d) => d.section('moment'),
      },
    ],
  },
];

/**
 * Vrai si l'entrée est proposée à ce profil.
 *
 * DEUX conditions, et c'est le cœur du lot N6 : la condition historique de
 * rôle, inchangée, ET le droit de voir la SECTION correspondante — celui que la
 * propriétaire règle à l'écran. Sans le second terme, fermer une rubrique dans
 * le paramétrage n'aurait eu aucun effet sur la barre latérale, et l'écran de
 * droits aurait menti ; sans le premier, on aurait perdu les conditions de rôle
 * qui ne se réduisent pas à une section (le RQ pour le retrait d'urgence).
 *
 * La section d'une entrée est le code de sa vue, sauf mention contraire.
 */
export const entreeVisible = (e: EntreeNav, d: DroitsNav) =>
  (e.visible ? e.visible(d) : true) && d.section(e.section || e.view);

/** Les familles réellement affichables, vidées de ce que ce profil ne voit pas. */
export function famillesVisibles(d: DroitsNav): FamilleNav[] {
  return FAMILLES_NAV
    .map((f) => ({ ...f, entrees: f.entrees.filter((e) => entreeVisible(e, d)) }))
    .filter((f) => f.entrees.length > 0);
}

/** Ce que la BARRE affiche : les entrées visibles, moins celles qui ont déménagé
 *  dans le menu du profil. La palette, elle, consomme `famillesVisibles`. */
export function famillesBarre(d: DroitsNav): FamilleNav[] {
  return famillesVisibles(d)
    .map((f) => ({ ...f, entrees: f.entrees.filter((e) => !e.horsBarre) }))
    .filter((f) => f.entrees.length > 0);
}

/**
 * L'écran de rattachement dans la barre : la fiche document, l'éditeur et la
 * revue n'ont pas d'entrée à eux — ils appartiennent à « Documents ». Sans
 * cette réduction, ouvrir une fiche referme la famille qui la contient.
 */
export function vueDeNavigation(v: GedView): GedView {
  if (['doc', 'review'].includes(v)) return 'list';
  // Les trois registres du catalogue (18/10/2026) : la barre éclaire « Référentiel des analyses ».
  if (['criteres', 'revueref', 'refperf'].includes(v)) return 'manuel';
  return v;
}

/**
 * Le NOM d'un écran, pour les phrases qui doivent le citer — l'infobulle de la
 * flèche « revenir », par exemple. Dérivé du modèle : un second dictionnaire de
 * libellés finirait par dire « Non-conformités » là où la barre dit autre chose.
 * Les écrans sans entrée de barre (fiche document, éditeur, revue) ont leur nom
 * en propre, sinon la flèche annoncerait « l'écran précédent » sur les cibles
 * les plus fréquentes.
 */
const HORS_BARRE: Partial<Record<GedView, string>> = {
  doc: 'la fiche du document',
  review: 'le circuit de revue',
  auditview: 'la vue auditeur',
  monplanning: 'mon planning',
  mespresences: 'mes présences',
  meshabilitations: 'mes habilitations',
  monparcours: 'mon parcours',
  parcours: 'le parcours d’habilitation',
  mondossier: 'mon dossier RH',
  mesconges: 'mes congés',
};

export function libelleVue(v: GedView): string {
  const cible = vueDeNavigation(v);
  if (cible === ACCUEIL.view) return ACCUEIL.label;
  for (const f of FAMILLES_NAV) {
    const e = f.entrees.find((x) => x.view === cible);
    if (e) return e.label;
  }
  return HORS_BARRE[v] || 'l’écran précédent';
}

/** Famille contenant l'écran courant — `null` pour l'accueil épinglé. */
export function familleDe(view: GedView): FamilleNav | null {
  const cible = vueDeNavigation(view);
  // Une entrée hors barre n'a pas de famille à ouvrir : déplier « Personnel &
  // plannings » en arrivant sur « Mon dossier RH » désignerait une entrée que
  // la barre ne contient plus.
  return FAMILLES_NAV.find((f) => f.entrees.some((e) => e.view === cible && !e.horsBarre)) || null;
}
